import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { abacatePayService } from '../services/abacatePayService';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { ThemedCard } from '@/components/ui/themed-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  CreditCardIcon, 
  WalletIcon, 
  ShoppingCartIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  AlertTriangleIcon,
  UploadIcon,
  CopyIcon,
  QrCodeIcon,
  RefreshCwIcon
} from 'lucide-react';

interface CreditPurchase {
  id: string;
  amount: number;
  credits: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  payment_proof_url?: string;
  abacate_billing_id?: string;
  created_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  credits_balance: number;
}

interface AbacatePayment {
  billingId: string;
  pixCode: string;
  qrCodeImage?: string;
  amount: number;
  expiresAt: Date;
  paymentUrl?: string;
}

const CreditsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(10);
  const [purchases, setPurchases] = useState<CreditPurchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPixModal, setShowPixModal] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState<CreditPurchase | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofError, setProofError] = useState<string>('');
  const [uploadingProof, setUploadingProof] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // AbacatePay states
  const [abacatePayment, setAbacatePayment] = useState<AbacatePayment | null>(null);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
  const [pixExpiryTime, setPixExpiryTime] = useState<number>(30 * 60); // 30 minutes in seconds
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerTaxId, setCustomerTaxId] = useState<string>('');
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (data && !error) {
      setProfile(data);
    }
  };

  const fetchPurchases = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('credit_purchases')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (data && !error) {
      setPurchases(data);
    }
  };

  // Cleanup polling on unmount or when modal closes
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        console.log('Cleaning up payment polling...');
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, [pollingInterval]);

  // Start PIX expiry timer
  useEffect(() => {
    if (showPixModal && pixExpiryTime > 0) {
      const timer = setTimeout(() => {
        setPixExpiryTime(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showPixModal, pixExpiryTime]);

  const handlePurchaseCredits = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Se AbacatePay estiver ativo e não temos telefone ou CPF, mostrar modal
    if (abacatePayService.isActive() && (!customerPhone || !customerTaxId)) {
      setShowPhoneModal(true);
      return;
    }

    setLoading(true);
    try {
      // First create the purchase record in our database
      const { data: purchaseData, error: purchaseError } = await supabase
        .from('credit_purchases')
        .insert({
          user_id: user.id,
          amount: creditAmount,
          credits: creditAmount,
          status: 'pending'
        })
        .select()
        .single();

      if (purchaseError) throw purchaseError;

      setCurrentPurchase(purchaseData);

      // Check if AbacatePay is enabled
      if (abacatePayService.isActive()) {
        try {
          // Create PIX payment via AbacatePay
          const payment = await abacatePayService.createPixPayment({
            participationId: purchaseData.id, // Using credit_purchase ID
            amount: creditAmount * 100, // Convert to cents
            customerName: profile?.full_name || user.email?.split('@')[0] || 'Cliente',
            customerEmail: user.email || 'cliente@example.com',
            customerPhone: customerPhone || '00000000000', // Use the captured phone
            customerTaxId: customerTaxId || undefined, // Use the captured CPF
            description: `Compra de ${creditAmount} créditos - Rifa Tudo`,
            externalId: `credits_${purchaseData.id}`
          });

          console.log('Payment response received:', payment);

          // Update purchase with AbacatePay billing ID
          if (payment.billingId) {
            await supabase
              .from('credit_purchases')
              .update({
                abacate_billing_id: payment.billingId
              })
              .eq('id', purchaseData.id);

            // Log payment details for debugging
            console.log('Payment details:', {
              billingId: payment.billingId,
              pixCode: payment.pixCode,
              qrCodeImage: payment.qrCodeImage ? 'Present' : 'Missing',
              qrCodeLength: payment.qrCodeImage?.length || 0
            });
            
            setAbacatePayment(payment);
            
            // Start polling for payment status
            startPaymentPolling(payment.billingId);
            
            // Show PIX modal with payment data
            setShowPixModal(true);
            fetchPurchases();
          } else {
            console.error('No billingId received from payment:', payment);
            throw new Error('Falha ao obter ID do pagamento');
          }
        } catch (abacateError: any) {
          console.error('AbacatePay error:', abacateError);
          // Check if it's a CPF error
          if (abacateError.message?.includes('CPF inválido')) {
            setErrorMessage('CPF inválido. Por favor, verifique o número digitado.');
            setShowErrorDialog(true);
            setShowPixModal(false);
            // Reopen phone modal to fix data
            setShowPhoneModal(true);
          } else {
            // Fallback to manual PIX if AbacatePay fails
            setErrorMessage('Erro ao gerar PIX automático. Tente novamente com dados válidos.');
            setShowErrorDialog(true);
          }
          return; // Exit early on error
        }
      } else {
        // If AbacatePay is not active, show modal for manual payment
        setShowPixModal(true);
        fetchPurchases();
      }
    } catch (error) {
      console.error('Error creating purchase:', error);
      setErrorMessage('Erro ao criar pedido de compra. Tente novamente.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const startPaymentPolling = (billingId: string) => {
    console.log('Starting payment polling for billingId:', billingId);
    
    if (!billingId) {
      console.error('Cannot start polling: billingId is undefined');
      return;
    }
    
    // Poll every 3 seconds for better UX
    const interval = setInterval(async () => {
      try {
        // Check status via AbacatePay API
        const statusResponse = await abacatePayService.getPaymentStatus(billingId);
        console.log('Payment status check:', statusResponse);
        
        if (statusResponse.status === 'PAID') {
          clearInterval(interval);
          setPollingInterval(null);
          
          // Payment confirmed!
          setSuccessMessage(`✅ Pagamento confirmado! ${creditAmount} créditos foram adicionados à sua conta.`);
          setShowSuccessDialog(true);
          setShowPixModal(false);
          
          // Clear payment data
          setAbacatePayment(null);
          setCurrentPurchase(null);
          
          // Refresh user data
          fetchProfile();
          fetchPurchases();
          
          // Play success sound (optional)
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn97Zs2IUCTyE2Oy9diMFBCuBztbHei4KFYnU3raILBAoevXvpHkVC');
          audio.play().catch(() => {}); // Ignore errors
        } else if (statusResponse.status === 'EXPIRED' || statusResponse.status === 'CANCELLED') {
          clearInterval(interval);
          setPollingInterval(null);
          
          setErrorMessage(
            statusResponse.status === 'EXPIRED' 
              ? '⏰ Pagamento expirado. Por favor, tente novamente.'
              : '❌ Pagamento cancelado.'
          );
          setShowErrorDialog(true);
          setShowPixModal(false);
          
          // Clear payment data
          setAbacatePayment(null);
          setCurrentPurchase(null);
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on error
      }
    }, 3000); // Check every 3 seconds

    setPollingInterval(interval);
  };

  const checkPaymentStatus = async () => {
    if (!abacatePayment) return;

    setCheckingPaymentStatus(true);
    try {
      const status = await abacatePayService.getPaymentStatus(abacatePayment.billingId);
      console.log('Manual payment status check:', status);
      
      if (status.status === 'PAID') {
        // Stop polling
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        
        setSuccessMessage('✅ Pagamento confirmado! Créditos adicionados.');
        setShowSuccessDialog(true);
        setShowPixModal(false);
        setAbacatePayment(null);
        setCurrentPurchase(null);
        fetchProfile();
        fetchPurchases();
      } else if (status.status === 'EXPIRED' || status.status === 'CANCELLED') {
        setErrorMessage(
          status.status === 'EXPIRED' 
            ? '⏰ Pagamento expirado.'
            : '❌ Pagamento cancelado.'
        );
      } else {
        setSuccessMessage('⏳ Pagamento ainda não confirmado. Continue aguardando...');
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setErrorMessage('Erro ao verificar status. Tente novamente.');
    } finally {
      setCheckingPaymentStatus(false);
    }
  };

  // Função para validar CPF
  const isValidCPF = (cpf: string): boolean => {
    // Remove caracteres não numéricos
    cpf = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos
    if (cpf.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    
    // Valida dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let digit = 11 - (sum % 11);
    if (digit > 9) digit = 0;
    if (parseInt(cpf.charAt(9)) !== digit) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    digit = 11 - (sum % 11);
    if (digit > 9) digit = 0;
    if (parseInt(cpf.charAt(10)) !== digit) return false;
    
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setProofError('O arquivo deve ter no máximo 5MB');
        return;
      }
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setProofError('Por favor, envie uma imagem (JPG, PNG) ou PDF');
        return;
      }
      
      setProofFile(file);
      setProofError('');
    }
  };

  const handleProofUpload = async () => {
    if (!proofFile || !currentPurchase) return;

    setUploadingProof(true);
    try {
      // Upload proof file to Supabase Storage with unique timestamp
      const fileExt = proofFile.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `${user?.id}/${currentPurchase.id}_${timestamp}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('payment-proofs')
        .upload(fileName, proofFile, {
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Save just the file path, not the full URL
      // We'll generate signed URLs when needed
      const { error: updateError } = await supabase
        .from('credit_purchases')
        .update({
          payment_proof_url: fileName, // Save path, not URL
          status: 'pending'
        })
        .eq('id', currentPurchase.id);

      if (updateError) throw updateError;

      setSuccessMessage('Comprovante enviado com sucesso! Aguarde a aprovação.');
      setShowSuccessDialog(true);
      setShowPixModal(false);
      setCurrentPurchase(null);
      setProofFile(null);
      setProofError('');
      fetchPurchases();
    } catch (error) {
      console.error('Error uploading proof:', error);
      alert('Erro ao enviar comprovante. Tente novamente.');
    } finally {
      setUploadingProof(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };

    const statusLabels: Record<string, string> = {
      pending: 'Pendente',
      approved: 'Aprovado',
      rejected: 'Rejeitado'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-700'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getStatusIcon = (status: string) => {
    const iconClasses = 'h-4 w-4';
    switch (status) {
      case 'pending':
        return <ClockIcon className={`${iconClasses} text-yellow-600`} />;
      case 'approved':
        return <CheckCircleIcon className={`${iconClasses} text-green-600`} />;
      case 'rejected':
        return <XCircleIcon className={`${iconClasses} text-red-600`} />;
      default:
        return <ClockIcon className={`${iconClasses} text-gray-600`} />;
    }
  };

  if (!user) {
    return (
      <BaseLayout variant="centered">
        <div className="min-h-[50vh] flex items-center justify-center">
          <ThemedCard className="p-8 text-center">
            <AlertTriangleIcon className="mx-auto h-12 w-12 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Login Necessário
            </h2>
            <p className="text-muted-foreground mb-4">
              Você precisa estar logado para comprar créditos.
            </p>
            <Button onClick={() => navigate('/auth/login')}>
              Fazer Login
            </Button>
          </ThemedCard>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout variant="wide">
      <div className="space-y-8">
        {/* Header */}
        <motion.div 
          className="text-center py-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Meus Créditos 💰
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Gerencie seus créditos e compre novos pacotes para participar de rifas.
          </p>
        </motion.div>

        {/* Saldo Atual */}
        {profile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ThemedCard className="p-8 bg-gradient-to-br from-green-500/10 to-emerald-600/10 border-green-500/20 text-center">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Saldo Atual</h2>
              <div className="flex items-center justify-center gap-4">
                <WalletIcon className="h-12 w-12 text-green-600" />
                <div className="text-5xl font-bold text-green-600">
                  {formatCurrency(profile.credits_balance || 0)}
                </div>
              </div>
              <p className="text-muted-foreground mt-2">Disponível para uso em rifas</p>
            </ThemedCard>
          </motion.div>
        )}

        {/* Comprar Créditos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ThemedCard className="p-8">
            <h2 className="text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
              <ShoppingCartIcon className="h-6 w-6" />
              Comprar Créditos
            </h2>
            
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              {[10, 25, 50, 100].map((amount) => (
                <ThemedCard 
                  key={amount}
                  className={`p-6 cursor-pointer transition-all text-center ${
                    creditAmount === amount
                      ? 'ring-2 ring-primary border-primary bg-primary/5 scale-105'
                      : 'hover:border-primary/50 hover:scale-102'
                  }`}
                  onClick={() => setCreditAmount(amount)}
                >
                  <div className="text-2xl font-bold text-foreground mb-2">
                    {formatCurrency(amount)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {amount} créditos
                  </div>
                  {creditAmount === amount && (
                    <CheckCircleIcon className="h-5 w-5 text-primary mx-auto mt-2" />
                  )}
                </ThemedCard>
              ))}
            </div>
            
            <Button
              size="lg"
              onClick={handlePurchaseCredits}
              disabled={loading || creditAmount < 5}
              className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processando...
                </div>
              ) : (
                `🛒 Comprar ${formatCurrency(creditAmount)}`
              )}
            </Button>
          </ThemedCard>
        </motion.div>

        {/* Histórico de Compras */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ThemedCard className="p-8">
            <h2 className="text-2xl font-semibold text-foreground mb-6">Histórico de Compras</h2>
            
            {purchases.length === 0 ? (
              <div className="text-center py-12">
                <CreditCardIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">
                  Você ainda não fez nenhuma compra de créditos.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {purchases.map((purchase) => (
                  <ThemedCard key={purchase.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-foreground">
                            {formatCurrency(purchase.amount)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {purchase.credits} créditos
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(purchase.created_at).toLocaleDateString('pt-BR')}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusIcon(purchase.status)}
                            {getStatusBadge(purchase.status)}
                          </div>
                        </div>
                      </div>
                      
                      {purchase.status === 'pending' && (
                        <Button
                          onClick={() => {
                            setCurrentPurchase(purchase);
                            setShowPixModal(true);
                          }}
                        >
                          Pagar
                        </Button>
                      )}
                    </div>
                  </ThemedCard>
                ))}
              </div>
            )}
          </ThemedCard>
        </motion.div>
      </div>

      {/* Phone Modal */}
      <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Informações para PIX</DialogTitle>
            <DialogDescription>
              Precisamos de alguns dados para gerar o PIX.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Telefone (com DDD)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(11) 98765-4321"
                value={customerPhone}
                onChange={(e) => {
                  // Remove non-numeric characters and format
                  const cleaned = e.target.value.replace(/\D/g, '');
                  let formatted = cleaned;
                  
                  if (cleaned.length >= 2) {
                    formatted = `(${cleaned.slice(0, 2)}) `;
                    if (cleaned.length > 2) {
                      formatted += cleaned.slice(2, 7);
                      if (cleaned.length > 7) {
                        formatted += `-${cleaned.slice(7, 11)}`;
                      }
                    }
                  }
                  
                  setCustomerPhone(formatted);
                }}
                maxLength={15}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="taxId">CPF</Label>
              <Input
                id="taxId"
                type="text"
                placeholder="123.456.789-00"
                value={customerTaxId}
                onChange={(e) => {
                  // Remove non-numeric characters and format
                  const cleaned = e.target.value.replace(/\D/g, '');
                  let formatted = cleaned;
                  
                  if (cleaned.length > 3) {
                    formatted = cleaned.slice(0, 3) + '.';
                    if (cleaned.length > 3) {
                      formatted += cleaned.slice(3, 6);
                      if (cleaned.length > 6) {
                        formatted += '.' + cleaned.slice(6, 9);
                        if (cleaned.length > 9) {
                          formatted += '-' + cleaned.slice(9, 11);
                        }
                      }
                    }
                  }
                  
                  setCustomerTaxId(formatted);
                }}
                maxLength={14}
              />
              <p className="text-sm text-muted-foreground">
                Estes dados são necessários para processar o pagamento PIX.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPhoneModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                const cleanPhone = customerPhone.replace(/\D/g, '');
                const cleanTaxId = customerTaxId.replace(/\D/g, '');
                
                if (cleanPhone.length < 10) {
                  alert('Por favor, insira um telefone válido com DDD.');
                  return;
                }
                
                if (cleanTaxId.length !== 11) {
                  alert('Por favor, insira um CPF válido com 11 dígitos.');
                  return;
                }
                
                // Validação de CPF
                if (!isValidCPF(cleanTaxId)) {
                  alert('CPF inválido. Por favor, verifique os números digitados.');
                  return;
                }
                
                setShowPhoneModal(false);
                handlePurchaseCredits();
              }}
              disabled={customerPhone.replace(/\D/g, '').length < 10 || customerTaxId.replace(/\D/g, '').length !== 11}
            >
              Continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PIX Payment Modal */}
      {showPixModal && currentPurchase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-hidden">
          <motion.div
            className="bg-background rounded-lg shadow-lg max-w-lg w-full max-h-[85vh] flex flex-col"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <ThemedCard className="border-0 shadow-none flex flex-col h-full">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
                <h2 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                  <QrCodeIcon className="h-6 w-6" />
                  Pagamento via PIX
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Stop polling when closing modal
                    if (pollingInterval) {
                      clearInterval(pollingInterval);
                      setPollingInterval(null);
                    }
                    setShowPixModal(false);
                    setCurrentPurchase(null);
                    setAbacatePayment(null);
                    setProofFile(null);
                    setProofError('');
                  }}
                >
                  ×
                </Button>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
                <div className="text-center">
                  <div className="text-lg font-semibold text-foreground mb-2">
                    Valor: {formatCurrency(currentPurchase.amount)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {currentPurchase.credits} créditos
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Show AbacatePay PIX if available */}
                  {abacatePayment ? (
                    <>
                      {/* Payment Status Indicator */}
                      <div className="text-center mb-4">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                          <div className="animate-pulse">
                            <ClockIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                            Aguardando pagamento...
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Estamos verificando seu pagamento automaticamente
                        </p>
                      </div>

                      {/* Timer */}
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-2">
                          Pagamento expira em:
                        </div>
                        <div className="text-2xl font-bold text-primary">
                          {Math.floor(pixExpiryTime / 60)}:{(pixExpiryTime % 60).toString().padStart(2, '0')}
                        </div>
                      </div>

                      {/* QR Code */}
                      <div className="text-center">
                        <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">1. Escaneie o QR Code</h3>
                        <div className="bg-muted p-4 sm:p-6 rounded-lg inline-block">
                          {abacatePayment.qrCodeImage ? (
                            <img 
                              src={abacatePayment.qrCodeImage} 
                              alt="QR Code PIX" 
                              className="w-40 h-40 sm:w-48 sm:h-48"
                            />
                          ) : (
                            <div className="w-40 h-40 sm:w-48 sm:h-48 bg-gray-200 rounded flex items-center justify-center">
                              <QrCodeIcon className="h-20 w-20 sm:h-24 sm:w-24 text-gray-400" />
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">QR Code PIX</p>
                        </div>
                      </div>

                      {/* PIX Copy Code */}
                      <div className="text-center">
                        <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">2. Ou copie o código PIX</h3>
                        <div className="bg-muted p-3 sm:p-4 rounded-lg">
                          <p className="text-xs font-mono break-all text-foreground/80">
                            {abacatePayment.pixCode}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => {
                              navigator.clipboard.writeText(abacatePayment.pixCode);
                              setSuccessMessage('Código PIX copiado!');
                            }}
                          >
                            <CopyIcon className="h-4 w-4 mr-2" />
                            Copiar código
                          </Button>
                        </div>
                      </div>

                      {/* Check Status Button */}
                      <Button
                        onClick={checkPaymentStatus}
                        disabled={checkingPaymentStatus}
                        className="w-full"
                        variant="outline"
                      >
                        {checkingPaymentStatus ? (
                          <div className="flex items-center gap-2">
                            <RefreshCwIcon className="h-4 w-4 animate-spin" />
                            Verificando...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <RefreshCwIcon className="h-4 w-4" />
                            Verificar Pagamento
                          </div>
                        )}
                      </Button>

                      <Alert className="bg-blue-50 border-blue-200">
                        <AlertDescription className="text-sm">
                          <strong>💡 Dica:</strong> Após o pagamento, o status será atualizado automaticamente em alguns segundos.
                        </AlertDescription>
                      </Alert>
                    </>
                  ) : (
                    /* Fallback to manual PIX if AbacatePay fails */
                    <>
                      <div className="text-center">
                        <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">1. Escaneie o QR Code</h3>
                        <div className="bg-muted p-4 sm:p-6 rounded-lg inline-block">
                          <div className="w-40 h-40 sm:w-48 sm:h-48 bg-gray-200 rounded flex items-center justify-center">
                            <QrCodeIcon className="h-20 w-20 sm:h-24 sm:w-24 text-gray-400" />
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">QR Code PIX Manual</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-base sm:text-lg font-medium text-foreground mb-3">2. Ou copie o código PIX</h3>
                        <div className="flex gap-2">
                      <Input
                        value="11999999999"
                        readOnly
                        className="font-mono text-xs"
                        placeholder="Chave PIX: 11999999999"
                      />
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText('11999999999');
                          setSuccessMessage('Chave PIX copiada!');
                          setShowSuccessDialog(true);
                        }}
                      >
                        <CopyIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base sm:text-lg font-medium text-foreground mb-3">3. Envie o comprovante</h3>
                    <div className="space-y-3">
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileChange}
                        className={proofError ? 'border-red-500' : ''}
                      />
                      {proofError && (
                        <p className="text-sm text-red-600">{proofError}</p>
                      )}
                      <Button
                        onClick={handleProofUpload}
                        disabled={!proofFile || uploadingProof || !!proofError}
                        className="w-full disabled:opacity-50"
                      >
                        <UploadIcon className="h-4 w-4 mr-2" />
                        {uploadingProof ? 'Enviando...' : 'Enviar Comprovante'}
                      </Button>
                    </div>
                  </div>

                      <Alert>
                        <AlertTriangleIcon className="h-4 w-4" />
                        <AlertDescription>
                          Após o pagamento, envie o comprovante para liberar seus créditos.
                          A aprovação é feita manualmente e pode levar até 24 horas.
                        </AlertDescription>
                      </Alert>
                    </>
                  )}
                </div>
              </div>
            </ThemedCard>
          </motion.div>
        </div>
      )}

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              Sucesso
            </DialogTitle>
            <DialogDescription className="pt-2">
              {successMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowSuccessDialog(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircleIcon className="h-5 w-5 text-red-600" />
              Erro
            </DialogTitle>
            <DialogDescription className="pt-2">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowErrorDialog(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </BaseLayout>
  );
};

export default CreditsPage;